import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  EditModeButtons,
  DeletedRowButtons,
  NormalRowButtons,
  HistoryButton,
} from "./SectionRowActionButtons";

/**
 * Remirror plugin: section row action toolbar.
 * When inToolbar is true: portaled into Remirror toolbar row and styled to match.
 * When inToolbar is false/undefined: renders normally (e.g. in col-2 when not editing).
 */
export default function RemirrorSectionRowToolbarPlugin({
  inToolbar,
  editMode,
  deleted,
  historyCount = 0,
  onSave,
  onCancel,
  onEdit,
  onDelete,
  onReset,
  onRestore,
  onHardDelete,
  onHistory,
}) {
  const ref = useRef(null);
  const [toolbarEl, setToolbarEl] = useState(null);

  useEffect(() => {
    if (!inToolbar) return;
    const root = ref.current?.parentElement;
    if (root?.firstElementChild) {
      setToolbarEl(root.firstElementChild);
    }
    return () => setToolbarEl(null);
  }, [inToolbar]);

  const buttons = editMode ? (
    <>
      <EditModeButtons onSave={onSave} onCancel={onCancel} />
      <HistoryButton onClick={onHistory} historyCount={historyCount} />
    </>
  ) : deleted ? (
    <>
      <DeletedRowButtons onRestore={onRestore} onHardDelete={onHardDelete} />
      <HistoryButton onClick={onHistory} historyCount={historyCount} />
    </>
  ) : (
    <>
      <NormalRowButtons onEdit={onEdit} onDelete={onDelete} onReset={onReset} />
      <HistoryButton onClick={onHistory} historyCount={historyCount} />
    </>
  );

  if (!inToolbar) {
    return <>{buttons}</>;
  }

  /* In toolbar: History at top with others; Save/Cancel below the editor */
  if (editMode) {
    const historyInToolbar = toolbarEl
      ? createPortal(
          <div className="remirror-section-row-toolbar-plugin">
            <HistoryButton onClick={onHistory} historyCount={historyCount} />
          </div>,
          toolbarEl
        )
      : null;
    return (
      <>
        <div ref={ref} style={{ display: "none" }} aria-hidden />
        {historyInToolbar}
        <div className="remirror-section-row-edit-actions">
          <EditModeButtons
            onSave={onSave}
            onCancel={onCancel}
            saveLabel="Sauvegarder"
            cancelLabel="Annuler"
          />
        </div>
      </>
    );
  }

  /* inToolbar + !editMode (shouldn't occur when used from RemirrorEditor) */
  const content = (
    <div ref={ref} className="remirror-section-row-toolbar-plugin">
      {buttons}
    </div>
  );
  if (toolbarEl) {
    return createPortal(content, toolbarEl);
  }
  return content;
}
